import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import {
  ArrowForward,
  CheckCircle,
  Devices,
  EmojiEvents,
  ErrorOutline,
  FiberManualRecord,
  MapOutlined,
  NotificationsActiveOutlined,
  People,
  Radar,
  RouteOutlined,
  ShieldOutlined,
  Speed,
  WarningAmber,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format, formatDistanceToNow } from 'date-fns'
import { StatCard } from '../../components/StatCard'
import { useDevices } from '../../hooks/useDevices'
import { useUsers } from '../../hooks/useUsers'
import { useAlerts } from '../../hooks/useRules'
import { useTwinDevices } from '../../hooks/useDigitalTwin'
import { useNotifications } from '../../hooks/useNotifications'
import { useAuthStore } from '../../store/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import { useMyTenant, useLeaderboard } from '../../hooks/useFleet'
import { countOnline } from '../../lib/deviceStatus'

function unwrap<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  const value = data as { devices?: T[]; data?: T[] } | undefined
  return value?.devices ?? value?.data ?? []
}

const panelHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  mb: 2.25,
}

export default function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const { isAdmin } = usePermissions()
  const { data: tenant } = useMyTenant(user?.tenant_id)
  const { data: leaderboard = [] } = useLeaderboard('weekly')
  const { data: devicesRaw, isLoading: devicesLoading } = useDevices()
  const { data: usersRaw, isLoading: usersLoading } = useUsers()
  const { data: alerts = [] } = useAlerts({ limit: 5 })
  const { data: twins = [] } = useTwinDevices()
  const { data: notifications = [] } = useNotifications()

  const devices = unwrap<{ id: string; name: string; status: string; vehicle_type?: string }>(devicesRaw)
  const users = unwrap<{ id: string }>(usersRaw)
  const deviceMap = new Map(devices.map((device) => [device.id, device]))
  const onlineDevices = countOnline(devices)
  const onlineRate = devices.length ? Math.round((onlineDevices / devices.length) * 100) : 0
  const movingDevices = twins.filter((twin) => (twin.last_speed ?? 0) > 0).length
  const criticalAlerts = alerts.filter((alert) => alert.severity === 'critical' && !alert.acknowledged).length
  const unreadNotifications = notifications.filter((notification) => !notification.read).length
  const firstName = user?.name?.split(' ')[0] ?? t('dashboard.title')
  const translate = (key: string, fallback: string) => t(key, { defaultValue: fallback })

  if (devicesLoading || usersLoading) {
    return (
      <Box sx={{ minHeight: '55vh', display: 'grid', placeItems: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={34} thickness={4} />
          <Typography variant="body2" color="text.secondary">{translate('common.loading', 'Preparing your fleet workspace…')}</Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, sm: 3.5, lg: 4 },
          borderRadius: { xs: 3, sm: 4 },
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(118deg, #081B2E 0%, #0B3B5E 58%, #0C78B6 100%)',
          boxShadow: '0 24px 54px rgba(8, 46, 75, .18)',
          '&::before': {
            content: '""', position: 'absolute', width: 330, height: 330, borderRadius: '50%',
            right: -90, top: -190, border: '1px solid rgba(255,255,255,.14)',
            boxShadow: '0 0 0 62px rgba(255,255,255,.035), 0 0 0 124px rgba(255,255,255,.018)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', gap: 3, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box>
            <Chip
              icon={<Radar sx={{ color: '#73E2C1 !important' }} />}
              label={translate('dashboard.liveOperations', 'LIVE OPERATIONS')}
              size="small"
              sx={{ mb: 2, color: '#DDF9F1', bgcolor: 'rgba(32,201,151,.13)', border: '1px solid rgba(124,232,200,.22)' }}
            />
            <Typography variant="h3" sx={{ fontSize: { xs: 30, sm: 40 }, lineHeight: 1.08 }}>
              {translate('dashboard.welcomeBack', 'Welcome back')}, {firstName}
            </Typography>
            <Typography sx={{ mt: 1.25, color: 'rgba(232,244,255,.68)', maxWidth: 650 }}>
              {translate('dashboard.commandSubtitle', 'Your fleet is connected. Here is what needs your attention today.')} · {format(new Date(), 'EEEE, MMMM d')}
            </Typography>
            <Stack direction="row" spacing={1.25} sx={{ mt: 3 }}>
              <Button variant="contained" color="primary" startIcon={<MapOutlined />} onClick={() => navigate('/map')}>
                {translate('nav.liveMap', 'Open live map')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Devices />}
                onClick={() => navigate('/devices')}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,.24)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,.06)' } }}
              >
                {translate('nav.allDevices', 'View devices')}
              </Button>
            </Stack>
          </Box>

          <Box sx={{ minWidth: { xs: '100%', md: 260 }, p: 2.25, borderRadius: 3, bgcolor: 'rgba(4,20,34,.28)', border: '1px solid rgba(255,255,255,.11)', backdropFilter: 'blur(10px)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
              <Typography variant="body2" sx={{ color: 'rgba(235,247,255,.68)' }}>{translate('dashboard.fleetAvailability', 'Fleet availability')}</Typography>
              <Typography variant="h5">{onlineRate}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={onlineRate} sx={{ height: 8, borderRadius: 8, bgcolor: 'rgba(255,255,255,.10)', '& .MuiLinearProgress-bar': { borderRadius: 8, background: 'linear-gradient(90deg, #20C997, #7CE8C8)' } }} />
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
              <Box><Typography variant="h6">{onlineDevices}</Typography><Typography variant="caption" sx={{ color: 'rgba(235,247,255,.58)' }}>{translate('dashboard.online', 'Online')}</Typography></Box>
              <Box><Typography variant="h6">{movingDevices}</Typography><Typography variant="caption" sx={{ color: 'rgba(235,247,255,.58)' }}>{translate('dashboard.moving', 'Moving')}</Typography></Box>
              <Box><Typography variant="h6">{criticalAlerts}</Typography><Typography variant="caption" sx={{ color: 'rgba(235,247,255,.58)' }}>{translate('dashboard.critical', 'Critical')}</Typography></Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2.25} sx={{ mb: 2.25 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title={translate('dashboard.devicesCount', 'Total fleet')} value={devices.length} subtitle={t('dashboard.onlineCount', { count: onlineDevices })} icon={<Devices />} color="#1597E5" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title={translate('dashboard.moving', 'Vehicles moving')} value={movingDevices} subtitle={translate('dashboard.liveMovement', 'Live movement detected')} icon={<Speed />} color="#20B987" />
        </Grid>
        {isAdmin && (
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard title={translate('dashboard.usersCount', 'Team members')} value={users.length} subtitle={translate('dashboard.inYourTenant', 'In your workspace')} icon={<People />} color="#7867E8" />
          </Grid>
        )}
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title={translate('dashboard.activeAlerts', 'Needs attention')} value={criticalAlerts} subtitle={t('dashboard.unreadNotifications', { count: unreadNotifications })} icon={<WarningAmber />} color="#EF5261" />
        </Grid>
      </Grid>

      <Grid container spacing={2.25}>
        <Grid item xs={12} lg={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 2.25, sm: 3 } }}>
              <Box sx={panelHeaderSx}>
                <Box><Typography variant="h6">{translate('dashboard.deviceStatus', 'Fleet pulse')}</Typography><Typography variant="body2" color="text.secondary">{translate('dashboard.liveVehicleHealth', 'Live vehicle health and connection status')}</Typography></Box>
                <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/devices')}>{translate('common.viewAll', 'View all')}</Button>
              </Box>
              {twins.length === 0 ? (
                <Alert severity="info" icon={<Radar />}>{translate('dashboard.noTwinData', 'Live status will appear as soon as devices begin reporting.')}</Alert>
              ) : (
                <Stack spacing={1}>
                  {twins.slice(0, 6).map((twin) => {
                    const device = deviceMap.get(twin.device_id)
                    const statusColor = twin.status === 'online' ? '#23B26D' : twin.status === 'idle' ? '#F6A723' : '#91A4BD'
                    return (
                      <Box key={twin.device_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.4, borderRadius: 2.5, bgcolor: 'action.hover', transition: 'transform 150ms ease', '&:hover': { transform: 'translateX(3px)' } }}>
                        <Avatar sx={{ width: 40, height: 40, bgcolor: `${statusColor}18`, color: statusColor }}><Devices fontSize="small" /></Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="body2" fontWeight={700} noWrap>{device?.name ?? twin.device_id.slice(0, 8)}</Typography><Typography variant="caption" color="text.secondary">{device?.vehicle_type ?? translate('dashboard.trackedVehicle', 'Tracked vehicle')}</Typography></Box>
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: .75, color: 'text.secondary' }}><Speed sx={{ fontSize: 16 }} /><Typography variant="caption">{Math.round(twin.last_speed ?? 0)} km/h</Typography></Box>
                        <Chip icon={<FiberManualRecord sx={{ fontSize: '10px !important' }} />} label={translate(`dashboard.${twin.status}`, twin.status)} size="small" sx={{ color: statusColor, bgcolor: `${statusColor}12`, '& .MuiChip-icon': { color: statusColor } }} />
                      </Box>
                    )
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 2.25, sm: 3 } }}>
              <Box sx={panelHeaderSx}>
                <Box><Typography variant="h6">{translate('dashboard.recentAlerts', 'Attention queue')}</Typography><Typography variant="body2" color="text.secondary">{translate('dashboard.alertsSubtitle', 'Prioritized events from across your fleet')}</Typography></Box>
                <Chip label={criticalAlerts ? `${criticalAlerts} ${translate('dashboard.critical', 'critical')}` : translate('dashboard.clear', 'All clear')} size="small" color={criticalAlerts ? 'error' : 'success'} />
              </Box>
              {alerts.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', borderRadius: 3, bgcolor: 'rgba(35,178,109,.07)' }}><CheckCircle sx={{ color: 'success.main', fontSize: 34, mb: 1 }} /><Typography variant="body2" fontWeight={700}>{translate('dashboard.noAlerts', 'No active alerts')}</Typography></Box>
              ) : (
                <Stack spacing={1}>
                  {alerts.slice(0, 5).map((alert) => {
                    const color = alert.severity === 'critical' ? '#EF5261' : alert.severity === 'warning' ? '#F6A723' : '#4B7BEC'
                    return (
                      <Box key={alert.id} sx={{ display: 'flex', gap: 1.25, p: 1.4, borderRadius: 2.5, bgcolor: 'action.hover' }}>
                        <Box sx={{ width: 34, height: 34, flex: '0 0 auto', display: 'grid', placeItems: 'center', borderRadius: 2, color, bgcolor: `${color}13` }}><ErrorOutline fontSize="small" /></Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="body2" fontWeight={650} noWrap>{alert.message}</Typography><Typography variant="caption" color="text.secondary">{alert.created_at ? formatDistanceToNow(new Date(alert.created_at), { addSuffix: true }) : '—'}</Typography></Box>
                        <Chip label={alert.severity} size="small" sx={{ color, bgcolor: `${color}10` }} />
                      </Box>
                    )
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={panelHeaderSx}><Typography variant="h6">{translate('dashboard.quickActions', 'Quick actions')}</Typography><ShieldOutlined color="primary" /></Box>
              <Grid container spacing={1.25}>
                {[
                  { icon: <MapOutlined />, label: translate('nav.liveMap', 'Live map'), path: '/map', color: '#1597E5' },
                  { icon: <RouteOutlined />, label: translate('nav.trips', 'Trips'), path: '/trips', color: '#7867E8' },
                  { icon: <NotificationsActiveOutlined />, label: translate('nav.notifications', 'Notifications'), path: '/notifications', color: '#EF5261' },
                  { icon: <Devices />, label: translate('nav.allDevices', 'Devices'), path: '/devices', color: '#20B987' },
                ].map((action) => (
                  <Grid item xs={6} key={action.path}>
                    <Button onClick={() => navigate(action.path)} sx={{ width: '100%', minHeight: 94, display: 'flex', flexDirection: 'column', gap: 1, color: 'text.primary', bgcolor: 'action.hover', '&:hover': { bgcolor: `${action.color}12`, color: action.color } }}><Box sx={{ color: action.color }}>{action.icon}</Box><Typography variant="caption" fontWeight={700}>{action.label}</Typography></Button>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={panelHeaderSx}><Typography variant="h6">{translate('dashboard.recentNotifications', 'Latest updates')}</Typography><Chip label={unreadNotifications} size="small" color="primary" /></Box>
              {notifications.length === 0 ? (
                <Alert severity="info" icon={false}>{translate('dashboard.noNotifications', 'No notifications yet')}</Alert>
              ) : (
                <Stack spacing={1}>
                  {notifications.slice(0, 4).map((notification) => (
                    <Box key={notification.id} sx={{ p: 1.35, borderRadius: 2.25, bgcolor: notification.read ? 'action.hover' : 'rgba(21,151,229,.07)', borderInlineStart: notification.read ? 'none' : '3px solid', borderColor: 'primary.main' }}><Typography variant="body2" fontWeight={notification.read ? 600 : 750} noWrap>{notification.title}</Typography><Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>{notification.message}</Typography></Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={panelHeaderSx}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><EmojiEvents sx={{ color: '#F6A723' }} /><Typography variant="h6">{translate('dashboard.leaderboard', 'Driver leaderboard')}</Typography></Box><Chip label={translate('dashboard.thisWeek', 'This week')} size="small" /></Box>
              {leaderboard.length === 0 ? (
                <Alert severity="info" icon={false}>{translate('common.noData', 'No score data yet')}</Alert>
              ) : (
                <List disablePadding>
                  {leaderboard.slice(0, 4).map((entry, index) => {
                    const scoreColor = entry.score >= 90 ? '#23B26D' : entry.score >= 70 ? '#F6A723' : '#EF5261'
                    return (
                      <ListItem key={entry.driver_user_id} disableGutters sx={{ py: .75 }}>
                        <ListItemAvatar sx={{ minWidth: 42 }}><Avatar sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 800, bgcolor: index === 0 ? '#FFF2D3' : 'action.hover', color: index === 0 ? '#A86D00' : 'text.secondary' }}>#{entry.rank}</Avatar></ListItemAvatar>
                        <ListItemText primary={<Typography variant="body2" fontWeight={700}>{entry.driver_user_id.slice(0, 8)}</Typography>} secondary={`${entry.trips} ${translate('trips.title', 'trips').toLowerCase()} · ${entry.distance_km.toFixed(1)} km`} />
                        <Chip label={entry.score} size="small" sx={{ minWidth: 42, color: scoreColor, bgcolor: `${scoreColor}12` }} />
                      </ListItem>
                    )
                  })}
                </List>
              )}
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2.5, bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><Box><Typography variant="caption" color="text.secondary">{translate('dashboard.organization', 'Organization')}</Typography><Typography variant="body2" fontWeight={700}>{tenant?.name ?? '—'}</Typography></Box><Chip label={tenant?.subscription_plan ?? tenant?.status ?? '—'} size="small" color="primary" variant="outlined" /></Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
